import { useCallback, useEffect, useRef, useState } from "react"
import { Loader2, Mic, MicOff, SendHorizontal, Square } from "lucide-react"

import { cn } from "@/lib/utils"
import { Textarea } from "@/components/ui/textarea"
import { useAudioRecorder } from "@/hooks/use-audio-recorder"

// A member telling a story talks for longer than the 60 s the shared voice
// field allows. Matches the server cap on the interview's own transcription
// endpoint (App::Member::MemberStoryInterviewsController::MAX_ANSWER_AUDIO_SECONDS).
const MAX_RECORDING_MS = 300_000

/**
 * Type or talk. The mic records, the server transcribes, and the transcript
 * lands in the textarea for the member to read before sending — never sent
 * straight through, because a mis-transcription is theirs to fix.
 *
 * `onSend(text, mode)` reports "spoken" when any part of the answer arrived by
 * mic, so the transcript records how the member actually answered. Returning
 * `false` from it puts the text back in the box.
 */
export function ChatComposer({ token, disabled = false, onSend }) {
  const [value, setValue] = useState("")
  const [usedVoice, setUsedVoice] = useState(false)
  const valueRef = useRef(value)
  valueRef.current = value
  const textareaRef = useRef(null)

  // Put the cursor back in the box whenever the composer becomes usable again:
  // on mount, and each time a new question arrives (`disabled` goes false while
  // an answer is in flight). Without it the member has to click into the field
  // for every one of the seven questions, and Enter-to-send below does nothing
  // because the focus is still on the send button.
  useEffect(() => {
    if (!disabled) textareaRef.current?.focus()
  }, [disabled])

  const handleTranscript = useCallback((transcript) => {
    const current = (valueRef.current || "").trimEnd()
    setValue(current ? `${current} ${transcript}` : transcript)
    setUsedVoice(true)
    // The member's next move is almost always to read it back and fix a word.
    textareaRef.current?.focus()
  }, [])

  const { isSupported, state, error, toggleRecording } = useAudioRecorder({
    onTranscript: handleTranscript,
    endpoint: `/app/member/member_story_interviews/${token}/transcriptions`,
    maxRecordingMs: MAX_RECORDING_MS,
  })

  const isRecording = state === "recording"
  const isBusy = state === "requesting" || state === "uploading"
  const isError = state === "error"

  // What a tap does is the hook's rule now, not this component's — see
  // useAudioRecorder#toggleRecording. A member mid-interview who fumbles the
  // permission prompt gets a retry, not a dead button.
  const handleMic = toggleRecording

  const submit = async () => {
    const text = value.trim()
    if (!text || disabled) return

    const mode = usedVoice ? "spoken" : "typed"
    setValue("")
    setUsedVoice(false)

    // Give the member their words back if the send failed. Retyping a
    // paragraph they just dictated because of a dropped connection is the one
    // thing that would make them abandon the interview.
    const accepted = await onSend(text, mode)
    if (accepted === false) {
      setValue(text)
      setUsedVoice(mode === "spoken")
    }
  }

  const micIcon = isBusy ? (
    <Loader2 className="h-4 w-4 animate-spin" />
  ) : isRecording ? (
    <Square className="h-3.5 w-3.5 fill-current" />
  ) : isError ? (
    <MicOff className="h-4 w-4" />
  ) : (
    <Mic className="h-4 w-4" />
  )

  return (
    <div className="shrink-0 border-t border-zinc-200 bg-white">
      <div className="mx-auto w-full max-w-2xl px-4 py-3">
        <div className="flex items-end gap-2">
          {isSupported && (
            <button
              type="button"
              onClick={handleMic}
              disabled={isBusy || disabled}
              aria-pressed={isRecording}
              aria-label={
                isRecording
                  ? "Stop recording"
                  : isError
                  ? "Try the microphone again"
                  : "Answer by talking"
              }
              title={
                isRecording
                  ? "Stop recording"
                  : isError
                  ? "Try again"
                  : "Answer by talking"
              }
              className={cn(
                "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kf-red/50",
                !isRecording && !isError && "bg-zinc-100 text-zinc-600 hover:bg-zinc-200",
                isRecording && "animate-pulse bg-kf-red text-white ring-2 ring-kf-red/30",
                isError && "bg-red-50 text-kf-red",
                (isBusy || disabled) && "cursor-not-allowed opacity-60"
              )}
            >
              {micIcon}
            </button>
          )}

          <Textarea
            ref={textareaRef}
            rows={1}
            value={value}
            disabled={disabled || isBusy}
            placeholder={isRecording ? "Listening…" : "Type your answer, or tap the mic"}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={(event) => {
              // Enter sends, Shift+Enter breaks the line — a story runs to
              // paragraphs, so the member needs both.
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault()
                submit()
              }
            }}
            className="max-h-40 min-h-10 flex-1 resize-none border-zinc-300 bg-white text-zinc-900 placeholder:text-zinc-400"
          />

          <button
            type="button"
            onClick={submit}
            disabled={disabled || !value.trim()}
            aria-label="Send answer"
            className={cn(
              "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kf-red/50",
              value.trim() && !disabled
                ? "bg-kf-red text-white hover:bg-kf-red/90"
                : "cursor-not-allowed bg-zinc-100 text-zinc-400"
            )}
          >
            <SendHorizontal className="h-4 w-4" />
          </button>
        </div>

        {error ? (
          // The escape hatch matters more than the error: talking is optional,
          // and nobody should feel stuck on a broken mic when the text box
          // right next to it works.
          <p className="mt-2 text-xs leading-relaxed text-kf-red">
            {error}{" "}
            <span className="text-zinc-500">
              You can always type your answer instead.
            </span>
          </p>
        ) : (
          <p className="mt-2 text-xs text-zinc-500">
            {isRecording
              ? "Recording — tap the square when you're done."
              : "Take your time. Your answers are saved as you go."}
          </p>
        )}
      </div>
    </div>
  )
}
